/**
 * @module services/notificationService
 * @description Toast notification service with Svelte store
 *
 * Provides a reactive notification system:
 * - Multiple notification types (success, error, warning, info)
 * - Auto-dismiss with configurable duration
 * - Manual dismiss support
 * - Maximum notification limit
 *
 * @example
 * import { notifications, notify } from '@services/notificationService';
 *
 * // Show notifications
 * notify.success('Item saved successfully');
 * notify.error('Failed to load items');
 * notify.warning('Connection lost');
 * notify.info('New items available');
 *
 * // In Svelte component
 * {#each $notifications as notification}
 *   <Toast {...notification} />
 * {/each}
 */

import { writable, derived, get } from 'svelte/store';
import { ui } from '@/config';
import type { Notification, NotificationType } from '@types';

// =============================================================================
// STORE
// =============================================================================

/** Internal notifications store */
const notificationsStore = writable<Notification[]>([]);

/** Exported readable notifications store */
export const notifications = {
  subscribe: notificationsStore.subscribe,
};

/** Notification count */
export const notificationCount = derived(
  notificationsStore,
  ($notifications) => $notifications.length
);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Generate unique notification ID
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get duration for notification type
 */
function getDuration(type: NotificationType, customDuration?: number): number {
  if (customDuration !== undefined) return customDuration;
  return ui.notificationDuration[type];
}

/**
 * Schedule auto-dismiss for a notification
 */
function scheduleAutoDismiss(id: string, duration: number): void {
  if (duration <= 0) return; // Persistent notification

  setTimeout(() => {
    dismiss(id);
  }, duration);
}

/**
 * Enforce maximum notifications limit
 */
function enforceLimit(): void {
  const current = get(notificationsStore);
  if (current.length >= ui.maxNotifications) {
    // Remove oldest notifications
    const toRemove = current.length - ui.maxNotifications + 1;
    notificationsStore.update((notifications) => notifications.slice(toRemove));
  }
}

// =============================================================================
// CORE FUNCTIONS
// =============================================================================

/**
 * Add a notification
 */
function add(type: NotificationType, message: string, duration?: number): string {
  enforceLimit();

  const id = generateId();
  const finalDuration = getDuration(type, duration);

  const notification: Notification = {
    id,
    type,
    message,
    duration: finalDuration,
    createdAt: Date.now(),
  };

  notificationsStore.update((notifications) => [...notifications, notification]);

  if (finalDuration > 0) {
    scheduleAutoDismiss(id, finalDuration);
  }

  return id;
}

/**
 * Dismiss a notification by ID
 */
export function dismiss(id: string): void {
  notificationsStore.update((notifications) => notifications.filter((n) => n.id !== id));
}

/**
 * Dismiss all notifications
 */
export function dismissAll(): void {
  notificationsStore.set([]);
}

/**
 * Dismiss notifications by type
 */
export function dismissByType(type: NotificationType): void {
  notificationsStore.update((notifications) => notifications.filter((n) => n.type !== type));
}

// =============================================================================
// NOTIFICATION METHODS
// =============================================================================

/**
 * Show a success notification
 */
function success(message: string, duration?: number): string {
  return add('success', message, duration);
}

/**
 * Show an error notification
 */
function error(message: string, duration?: number): string {
  return add('error', message, duration);
}

/**
 * Show a warning notification
 */
function warning(message: string, duration?: number): string {
  return add('warning', message, duration);
}

/**
 * Show an info notification
 */
function info(message: string, duration?: number): string {
  return add('info', message, duration);
}

// =============================================================================
// EXPORTED SERVICE
// =============================================================================

/**
 * Notification service with all methods
 */
export const notify = {
  success,
  error,
  warning,
  info,
  dismiss,
  dismissAll,
  dismissByType,
};

/**
 * Notification service class (for DI compatibility)
 */
export class NotificationService {
  success = success;
  error = error;
  warning = warning;
  info = info;
  dismiss = dismiss;
  dismissAll = dismissAll;
  dismissByType = dismissByType;

  /** Get current notifications */
  getAll(): Notification[] {
    return get(notificationsStore);
  }

  /** Subscribe to notifications */
  subscribe(callback: (notifications: Notification[]) => void): () => void {
    return notificationsStore.subscribe(callback);
  }
}

/** Singleton instance */
export const notificationService = new NotificationService();

export default notify;
